/*
   App.js -- Application global singleton
   
   This file serves as the core application initialization and configuration module.
   It sets up the Vue application with all necessary components, state management,
   routing, and third-party integrations.
 */

import {debug} from '@/paks/js-polyfill'
import {Chat} from '@/paks/js-chat'
import {Rest} from '@/paks/js-rest'
import {Net} from '@/paks/js-net'
import {Storage} from '@/paks/js-storage'
import {Log} from '@/paks/js-log'
import {Store, State} from '@/paks/vu-state'
import {Routes} from '@/paks/vu-routes'
import VuDrag from '@/paks/vu-drag'
import Calendar from 'v-calendar'
import VuCard from '@/paks/vu-card'
import VuClip from '@/paks/vu-clip'
import VuConfirm from '@/paks/vu-confirm'
import VuDate from '@/paks/vu-date'
import VuDiv from '@/paks/vu-div'
import VuFeedback from '@/paks/vu-feedback'
import VuForm from '@/paks/vu-form'
import VuHelp from '@/paks/vu-nav/Help.vue'
import VuInput from '@/paks/vu-input'
import VuIcon from '@/paks/vu-icon'
import VuInputGroup from '@/paks/vu-input-group'
import VuPanel from '@/paks/vu-panel'
import VuPick from '@/paks/vu-pick'
import VuProgress from '@/paks/vu-progress'
import VuSign from '@/paks/vu-sign'
import VuTable from '@/paks/vu-table'
import VuTabs from '@/paks/vu-tabs'
import VuTip from '@/paks/vu-tip'
import VuValidate from '@/paks/vu-validate'

import {Dash} from '@/paks/vu-dash'
import {Auth, AuthState} from '@/paks/vu-auth'
import {SidebarState} from '@/paks/vu-nav'
import CacheState from '@/state/CacheState.js'
import ContextState from '@/state/ContextState.js'
import AppState from './AppState.js'
import ConfigState from './ConfigState.js'
import RefState from './RefState.js'

import 'v-calendar/style.css'

/**
   Main application class that handles initialization and configuration
   of the Vue application and its dependencies.
 */
class AppClass {
    // Core application services and utilities
    chat = null
    config = {}
    log = Log
    params = {}
    routes = null
    started = new Date()
    storage = null

    /**
       Initializes the application with provided configuration and parameters
       @param {Object} params - Application parameters including Vue instance, router, etc.
       @param {Object} config - Application configuration settings
     */
    create(params, config) {
        let {chat, cognito, display, models, router, vue, vuetify, widgets} = params

        // Validate required parameters
        if (!vue || !router) {
            throw new Error('Missing required vue and router arguments')
        }
        this.vue = vue
        this.router = router
        this.vuetify = vuetify

        // Initialize core services
        this.routes = Routes
        this.storage = Storage
        this.chat = Chat

        // Set up storage and state management
        this.storage.init({prefix: config.name})
        Store.init({persist: `${config.name}-state`, lazy: true, config})
        this.routes.init(router, params.components)
        
        // Initialize application state stores
        Store.add('app', new AppState())
        Store.add('auth', new AuthState())
        Store.add('cache', new CacheState())
        Store.add('config', new ConfigState())
        Store.add('context', new ContextState())
        Store.add('ref', new RefState(), {reactive: false})
        Store.add('sidebar', new SidebarState())

        // Set up global references
        State.ref.app = this
        State.ref.vue = vue
        State.ref.router = router
        State.ref.vuetify = vuetify
        State.ref.params = params
        State.ref.widgets = widgets
        State.ref.chat = chat
        State.ref.models = models
        State.ref.routes = Routes

        // Configure application
        Object.assign(this.params, params)
        this.setConfig(config, display)
        Net.setConfig(config)
        Rest.setConfig(config)

        // Initialize authentication
        Auth.init(this, config.cognito, {
            account: models.Account,
            user: models.User,
            cognito: cognito,
        })
        Auth.parseUri()

        // Register Vue components and plugins
        vue.use(VuDrag)
        vue.use(Calendar, {componentPrefix: 'vu'})
        vue.component('vu-date', VuDate)
        vue.component('vu-card', VuCard)
        vue.component('vu-clip', VuClip)
        vue.component('vu-confirm', VuConfirm)
        vue.component('vu-div', VuDiv)
        vue.component('vu-feedback', VuFeedback)
        vue.component('vu-form', VuForm)
        vue.component('vu-help', VuHelp)
        vue.component('vu-input', VuInput)
        vue.component('vu-icon', VuIcon)
        vue.component('vu-input-group', VuInputGroup)
        vue.component('vu-panel', VuPanel)
        vue.component('vu-pick', VuPick)
        vue.component('vu-progress', VuProgress)
        vue.component('vu-table', VuTable)
        vue.component('vu-tabs', VuTabs)
        vue.component('vu-validate', VuValidate)
        vue.component('vu-sign', VuSign)
        vue.directive('tip', VuTip)
        vue.component('dash', Dash)

        // Initialize optional features
        if (params.schema) {
            State.app.setSchema(params.schema)
        }
        if (params.components) {
            for (let [name, component] of Object.entries(params.components)) {
                vue.component(name, component)
            }
        }
        if (params.cognito) {
            State.app.setCognito(params.cognito)
        }
        if (params.standardDisplay) {
            State.app.setStandardDisplay(params.standardDisplay)
        }
        if (params.display) {
            State.app.setFullDisplay(params.display)
        }
        if (params.chat) {
            this.chat.provide(params.chat)
            this.chat.init(config)
        }
        if (params.widgets) {
            this.widgets = params.widgets
            for (let component of Object.values(params.widgets)) {
                vue.component(component.__name, component)
            }
        }
    }

    /**
       Configures application settings and environment
       @param {Object} config - Application configuration
       @param {Object} display - Display settings {name, roles}
     */
    setConfig(config, display) {
        if (display.roles) {
            Object.assign(config, {roles: display.roles})
        }
        if (display.name) {
            config.name = display.name
        }
        Object.assign(State.config, config)

        // Log application startup information
        this.log.info(
            `Starting ${config.name} ${config.version || '1.0'}.${config.build || 0} built for "${config.profile}"`
        )
        if (config.profile != 'prod' || window.location.hostname == 'localhost') {
            debug(config.profile)
        }
        if (config.api) {
            State.app.setApi(config.api)
        }
        if (config.features) {
            State.app.setFeatures(config.features)
        }
        State.app.local = config.cognito ? false : true
    }
}

// Create and export singleton instance
const App = new AppClass()

export {App}

/**
   Initializes the application with provided parameters and configuration
   @param {Object} params - Application parameters
   @param {Object} config - Application configuration
   @returns {AppClass} The initialized application instance
 */
export async function initApp(params, config) {
    App.create(params, config)
    return App
}